import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import BookCard from "@/components/BookCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [sort, setSort] = useState(params.get("sort") || "recent");
  const [filter, setFilter] = useState(params.get("filter") || "");
  const [page, setPage] = useState(parseInt(params.get("page") || "1"));
  const [categories, setCategories] = useState([]);
  const limit = 12;

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { q: q || undefined, category, sort, page, limit, filter: filter || undefined };
    api.get("/books", { params: p })
      .then((r) => { setBooks(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
    const sp = {};
    if (q) sp.q = q;
    if (category !== "all") sp.category = category;
    if (sort !== "recent") sp.sort = sort;
    if (filter) sp.filter = filter;
    if (page > 1) sp.page = String(page);
    setParams(sp);
  }, [q, category, sort, page, filter, setParams]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="mb-12">
        <div className="label-eyebrow text-accent mb-3">Catalogue</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Tous les livres</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          Explorez l'ensemble des ouvrages de Baron Vincent de Paul.
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="catalog-search"
            placeholder="Rechercher un livre, un thème..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-9 rounded-sm bg-transparent"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger data-testid="catalog-category" className="rounded-sm bg-transparent">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
            <SelectTrigger data-testid="catalog-sort" className="rounded-sm bg-transparent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Plus récents</SelectItem>
              <SelectItem value="popular">Populaires</SelectItem>
              <SelectItem value="price_asc">Prix croissant</SelectItem>
              <SelectItem value="price_desc">Prix décroissant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={filter || "none"} onValueChange={(v) => { setFilter(v === "none" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="catalog-filter" className="rounded-sm bg-transparent"><SelectValue placeholder="Filtre" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun filtre</SelectItem>
              <SelectItem value="new">Nouveautés</SelectItem>
              <SelectItem value="popular">Populaires</SelectItem>
              <SelectItem value="promo">En promo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-sm text-muted-foreground mb-8" data-testid="catalog-count">
        {total} livre{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[2/3] bg-secondary/40 animate-pulse" />
          ))}
        </div>
      ) : books.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">Aucun livre trouvé.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {books.map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-16 flex justify-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-sm" data-testid="page-prev">Précédent</Button>
          <div className="px-4 py-2 text-sm">{page} / {totalPages}</div>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="rounded-sm" data-testid="page-next">Suivant</Button>
        </div>
      )}
    </div>
  );
}
